import type { DataStoreState, StrictTransferData, TransferState } from "./data-store";

/**
 * Create a selector for transfer data by ID.
 *
 * @typeParam T - Optional strict transfer shape for narrowing data fields
 * @param transferId - Transfer identifier
 * @returns Selector function
 */
export function selectTransfer<T extends Record<string, unknown> = Record<string, unknown>>(transferId: string) {
	return (
		state: DataStoreState
	): (TransferState & { data: StrictTransferData<T> | null; originalData: StrictTransferData<T> | null }) | undefined =>
		state.transfers.get(transferId) as
			| (TransferState & { data: StrictTransferData<T> | null; originalData: StrictTransferData<T> | null })
			| undefined;
}

/**
 * Create a selector for a typed transfer field value.
 *
 * @typeParam T - Optional strict transfer shape
 * @typeParam K - Field key (inferred from `field` when T is provided)
 * @param transferId - Transfer identifier
 * @param field - Field name (restricted to `keyof T` when strict)
 * @returns Selector function returning `T[K]` (or `unknown` when untyped)
 */
export function selectTransferField<
	T extends Record<string, unknown> = Record<string, unknown>,
	K extends string & keyof T = string & keyof T,
>(transferId: string, field: K) {
	return (state: DataStoreState): T[K] | undefined =>
		state.transfers.get(transferId)?.data?.[field] as T[K] | undefined;
}

/**
 * Create a selector for transfer loading state.
 *
 * @param transferId - Transfer identifier
 * @returns Selector function
 */
export function selectTransferLoading(transferId: string) {
	return (state: DataStoreState): boolean => state.transfers.get(transferId)?.isLoading ?? false;
}

/**
 * Create a selector for transfer error state.
 *
 * @param transferId - Transfer identifier
 * @returns Selector function
 */
export function selectTransferError(transferId: string) {
	return (state: DataStoreState): Error | null => state.transfers.get(transferId)?.error ?? null;
}

/**
 * Create a selector for transfer dirty state.
 *
 * @param transferId - Transfer identifier
 * @returns Selector function
 */
export function selectTransferDirty(transferId: string) {
	return (state: DataStoreState): boolean => state.transfers.get(transferId)?.isDirty ?? false;
}

/**
 * Selector for all transfer IDs.
 *
 * @param state - Store state
 * @returns Array of transfer IDs
 */
export function selectTransferIds(state: DataStoreState): string[] {
	return Array.from(state.transfers.keys());
}

/**
 * Selector for store version (useful for detecting any change).
 *
 * @param state - Store state
 * @returns Store version number
 */
export function selectVersion(state: DataStoreState): number {
	return state.version;
}
